import { type JSX } from "react";
import type { Action, LLMRequest } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { formatDateWithZone } from "../../utils/formatDate";
import { formatJSON } from "./llmRequestFormatting";

function truncate(text: string, limit: number): string {
  return text.length > limit ? `${text.substring(0, limit)}...` : text;
}

/**
 * Responses are stored as raw strings or as JSON; both are previewed at 300 characters.
 * Long strings are truncated as-is, anything else is pretty-printed first.
 */
function responsePreview(response: unknown): string {
  if (typeof response === "string" && response.length > 300) {
    return `${response.substring(0, 300)}...`;
  }
  return truncate(formatJSON(response), 300);
}

function LabelledValue({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-500">{label}</label>
      <p className={`mt-1 text-sm text-gray-900${mono ? " font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function ActionCost({ action }: { action: Action }): JSX.Element {
  const tokens = (action.total_prompt_tokens || 0) + (action.total_completion_tokens || 0);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-500">Action Cost</label>
      <div className="mt-1">
        <p className="text-sm font-medium text-gray-900">
          {formatCost(
            getTotalCostFromApi(action.total_input_cost_usd, action.total_output_cost_usd),
          )}
        </p>
        {tokens > 0 && (
          <p className="text-xs text-gray-500">{tokens.toLocaleString()} tokens</p>
        )}
      </div>
    </div>
  );
}

function DecisionInformation({ action }: { action: Action }): JSX.Element {
  const showResolved =
    action.resolved_action && action.resolved_action !== action.selected_action;
  const hasParameters =
    action.decision_parameters && Object.keys(action.decision_parameters).length > 0;

  return (
    <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
      <h3 className="text-lg font-medium text-purple-900">Decision Information</h3>

      {action.selected_action && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-purple-700">Selected Action</label>
            <p className="mt-1 text-sm font-mono text-purple-900">{action.selected_action}</p>
          </div>
          {showResolved && (
            <div>
              <label className="block text-sm font-medium text-purple-700">Resolved To</label>
              <p className="mt-1 text-sm font-mono text-purple-900">{action.resolved_action}</p>
            </div>
          )}
        </div>
      )}

      {action.decision_source && (
        <div>
          <label className="block text-sm font-medium text-purple-700">Decision Source</label>
          <p className="mt-1 text-sm text-purple-900">{action.decision_source}</p>
        </div>
      )}

      {hasParameters && (
        <div>
          <label className="block text-sm font-medium text-purple-700 mb-2">
            Decision Parameters
          </label>
          <div className="bg-white p-3 rounded-sm border border-purple-200">
            <pre className="text-sm text-purple-900 whitespace-pre-wrap overflow-x-auto">
              {formatJSON(action.decision_parameters)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionTitleBar({
  isDecision,
  error,
  onBack,
}: {
  isDecision: boolean;
  error: string | null;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-3">
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 mr-2"
          title="Back to Turn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h2 className="text-2xl font-bold text-gray-900">Action Details</h2>
        {isDecision && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
            Agent Decision
          </span>
        )}
      </div>
      {error && (
        <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-md">
          Using mock data - API not available
        </div>
      )}
    </div>
  );
}

export function ActionHeader({
  action,
  llmRequestCount,
  error,
  onBack,
}: {
  action: Action;
  llmRequestCount: number;
  error: string | null;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <ActionTitleBar
        isDecision={Boolean(action.is_decision)}
        error={error}
        onBack={onBack}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <LabelledValue label="Action ID" value={action.action_id} mono />
        <LabelledValue label="Action Type" value={action.action_type} />
        <LabelledValue label="Created At" value={formatDateWithZone(action.created_at)} />
        <LabelledValue label="LLM Requests" value={String(llmRequestCount)} />
        <ActionCost action={action} />
      </div>

      {action.is_decision && <DecisionInformation action={action} />}

      {action.rationale && (
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-500 mb-2">Rationale</label>
          <div className="bg-blue-50 p-4 rounded-md">
            <p className="text-gray-900">{action.rationale}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function RequestSummary({
  request,
  index,
  detailHref,
}: {
  request: LLMRequest;
  index: number;
  detailHref: string;
}): JSX.Element {
  const hasCost = Boolean(request.input_cost_usd || request.output_cost_usd);
  const tokens = (request.prompt_tokens || 0) + (request.completion_tokens || 0);

  return (
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-3">
        <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full">
          <span className="text-sm font-medium text-purple-600">{index + 1}</span>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">Request {request.llm_request_id}</p>
          <p className="text-sm text-gray-500">
            {request.model} • {formatDateWithZone(request.created_at)}
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-4">
        {hasCost && (
          <div className="text-right">
            <div className="text-sm font-medium text-green-700">
              {formatCost(getTotalCostFromApi(request.input_cost_usd, request.output_cost_usd))}
            </div>
            {tokens > 0 && (
              <div className="text-xs text-gray-500">{tokens.toLocaleString()} tokens</div>
            )}
          </div>
        )}
        <a href={detailHref} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
          View Details →
        </a>
      </div>
    </div>
  );
}

export function LLMRequestCard({
  request,
  index,
  detailHref,
}: {
  request: LLMRequest;
  index: number;
  detailHref: string;
}): JSX.Element {
  return (
    <div className="p-6">
      <RequestSummary request={request} index={index} detailHref={detailHref} />

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-500 mb-2">Prompt Preview</label>
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm text-gray-900">{truncate(request.prompt, 200)}</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-500 mb-2">Response Preview</label>
        <div className="bg-blue-50 p-3 rounded-md">
          <pre className="text-sm text-gray-900 whitespace-pre-wrap">
            {responsePreview(request.response)}
          </pre>
        </div>
      </div>
    </div>
  );
}
